import { events } from 'elc-service-bus';
import { computed, observable, action } from 'mobx';

import { ViewController } from '@estee/elc-service';
import { ICart } from '@estee/elc-service-bus/typings/src/service-bus-topics/events/CartInterfaces';

import { IUpdatePayload } from '~api/CartAPI/ICartApiSdk';
import CartSidebar from '~domain/entities/CartSidebar/CartSidebar';
import { ICartOverlayVisibilityProps } from '~domain/entities/CartSidebar/ICartSidebar';
import ICartItem, { defaultEmptyCollection } from '~domain/entities/CartItem/ICartItem';
import CartRepository from '~repositories/CartRepository/CartRepository';
import { eventEmitter } from '~sharedComponents/EventAdapter';

import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';

import FetchedCartInfoUseCase from '~use-cases/FetchedCartInfoUseCase';
import UpdateCartUseCase from '~use-cases/UpdateCartUseCase';
import RemoveItemUseCase from '~use-cases/RemoveItemUseCase';
import RemoveKitItemUseCase from '~use-cases/RemoveKitItemUseCase';
import RemoveOfferItemUseCase from '~use-cases/RemoveOfferItemUseCase';
import SetActiveTabUseCase from '~use-cases/SetActiveTabUseCase';
import SetItemAddedUseCase from '~use-cases/SetItemAddedUseCase';
import SetSidebarVisibilityUseCase from '~use-cases/SetSidebarVisibilityUseCase';

import {
    ICartSidebarViewControllerData,
    ICartSidebarViewController
} from './ICartSidebarViewController';

class CartSidebarViewController extends ViewController implements ICartSidebarViewController {
    @observable cartRepository: CartRepository;
    @observable cartSidebar: CartSidebar;

    constructor({ cartRepository, cartSidebar }: ICartSidebarViewController) {
        super();
        this.cartRepository = cartRepository;
        this.cartSidebar = cartSidebar;
    }

    @computed
    public get data(): ICartSidebarViewControllerData {
        const signedInUser = this.cartRepository.signedInUser;
        const {
            cartItems,
            cartOrderData,
            totalQty,
            totalPrice,
            cartTotals,
            pricedSkusQty,
            loyaltySkusQty,
            samplesQty,
            isTaxDetermined
        } = this.cartRepository.cart;
        const {
            isVisible,
            displayItemAddedText,
            activeTab,
            offerCount,
            displayCartOffersComponent,
            autoClose,
            displayMiniOverlay
        } = this.cartSidebar;

        return {
            cartItems,
            cartOrderData,
            cartTotals,
            samplesQty,
            pricedSkusQty,
            loyaltySkusQty,
            itemsQuantity: totalQty,
            totalPrice,
            isVisible,
            displayItemAddedText,
            displayCartOffersComponent,
            activeTab,
            offerCount,
            autoClose,
            displayMiniOverlay,
            signedInUser,
            isTaxDetermined
        };
    }

    @computed public get isCartLoading(): boolean {
        return this.cartRepository.isLoading || this.cartSidebar.isLoading;
    }

    @action
    public updateCart = async ({ skus, quantity }: IUpdatePayload) => {
        const res = await new UpdateCartUseCase({ skus, quantity }).execute();

        return res;
    };

    @action
    public loadCartInfo = async () => {
        await new FetchedCartInfoUseCase().execute();
    };

    @action
    public setVisibility = (props: ICartOverlayVisibilityProps): void => {
        new SetSidebarVisibilityUseCase(props).execute();
    };

    @action
    public disableItemAddedText = (): void => {
        const setItemAddedTextUseCase = new SetItemAddedUseCase(false);
        setItemAddedTextUseCase.execute();
    };

    @action
    public removeItem = async (props: ICartItem) => {
        const { skuId, isKitItem, kitInfo, isGiftItem, offerCode, collectionId, cartId } = props;

        const kitPayload = {
            cartId: kitInfo?.cartId as number,
            collection: kitInfo?.collection || defaultEmptyCollection,
            skuId
        };

        const itemPayload = {
            cartId,
            collectionId,
            skus: [skuId]
        };

        const res =
            !isGiftItem && !!offerCode
                ? await new RemoveOfferItemUseCase(offerCode).execute()
                : isKitItem
                ? await new RemoveKitItemUseCase(kitPayload).execute()
                : await new RemoveItemUseCase(itemPayload).execute();

        eventEmitter(events.CART_UPDATED, {} as ICart);

        return res;
    };

    @action
    public changeTab = (newState: number) => {
        const setActiveTabUseCase = new SetActiveTabUseCase(newState);
        setActiveTabUseCase.execute();
    };

    @action
    public redirectClick = (url: string) => {
        const cartRepository = diContainer.get(serviceNames.cartRepository);
        let eLabel = '';
        cartRepository.abortRequest = true;

        if (url.includes('viewcart') || url.includes('cart')) {
            eLabel = 'view_bag_cta';
        } else if (url.includes('checkout')) {
            eLabel = 'checkout_cta';
        }

        const cartDropdownEvent = new CustomEvent('cart.drawer.clicked', {
            detail: {
              cartDrawerCta: eLabel,
              cartDrawerAction: 'click',
              cartDrawerCategory: 'cart_dropdown'
            }
        });
        window.dispatchEvent(cartDropdownEvent);

        const path = window.location.pathname;
        window.location.href = this.getRedirectUrl(path, url);
    };

    public getRedirectUrl = (currentPath: string, url: string): string => {
        //checks `/ + two letters + / + two letters`
        const hubRegex = /(\/[A-z]{2})?(\/[A-z]{2})?\//g;
        const pathMatches = currentPath.match(hubRegex);
        const redirectUrl = !!pathMatches ? pathMatches[0] + url.replace(/^\//, '') : url;

        return redirectUrl;
    };
}

export default CartSidebarViewController;
